import React from "react";
import { Box, Chip, Typography, alpha, useTheme } from "@mui/material";

/** Per-mode accent palette for the generation-mode tab bar. */
const CATEGORY_TAB_ACCENTS = {
  "text-to-video": {
    main: "#6366F1",
    gradient: "linear-gradient(135deg, #6366F1 0%, #818CF8 100%)",
    glow: "rgba(99, 102, 241, 0.45)",
  },
  "image-to-video": {
    main: "#A855F7",
    gradient: "linear-gradient(135deg, #9333EA 0%, #C084FC 100%)",
    glow: "rgba(168, 85, 247, 0.45)",
  },
  "video-to-video": {
    main: "#F97316",
    gradient: "linear-gradient(135deg, #EA580C 0%, #FB923C 100%)",
    glow: "rgba(249, 115, 22, 0.45)",
  },
  "music-to-video": {
    main: "#EC4899",
    gradient: "linear-gradient(135deg, #DB2777 0%, #F472B6 100%)",
    glow: "rgba(236, 72, 153, 0.45)",
  },
  "motion-sync": {
    main: "#14B8A6",
    gradient: "linear-gradient(135deg, #0D9488 0%, #2DD4BF 50%, #06B6D4 100%)",
    glow: "rgba(20, 184, 166, 0.5)",
  },
};

const DEFAULT_ACCENT = CATEGORY_TAB_ACCENTS["text-to-video"];

function CategoryTab({ cat, selected, onSelect, lang, isDark }) {
  const Icon = cat.icon;
  const label = lang?.[cat.labelKey] || cat.fallback;
  const accent = CATEGORY_TAB_ACCENTS[cat.id] || DEFAULT_ACCENT;

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onSelect(cat.id)}
      aria-pressed={selected}
      aria-label={label}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1.25,
        px: { xs: 1.5, sm: 2 },
        py: 1.1,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: selected
          ? alpha(accent.main, isDark ? 0.65 : 0.55)
          : alpha(accent.main, isDark ? 0.18 : 0.22),
        bgcolor: selected
          ? alpha(accent.main, isDark ? 0.22 : 0.1)
          : alpha(accent.main, isDark ? 0.06 : 0.04),
        color: selected ? accent.main : "text.secondary",
        cursor: "pointer",
        flexShrink: 0,
        font: "inherit",
        textAlign: "left",
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease",
        boxShadow: selected ? `0 6px 20px ${accent.glow}` : "none",
        transform: selected ? "translateY(-2px) scale(1.02)" : "none",
        "&::before": selected
          ? {
              content: '""',
              position: "absolute",
              inset: 0,
              background: accent.gradient,
              opacity: isDark ? 0.14 : 0.08,
              pointerEvents: "none",
            }
          : undefined,
        "&:hover": {
          borderColor: alpha(accent.main, 0.55),
          bgcolor: alpha(accent.main, isDark ? 0.16 : 0.1),
          color: accent.main,
          transform: selected ? "translateY(-2px) scale(1.02)" : "translateY(-1px)",
          boxShadow: `0 8px 22px ${alpha(accent.glow, 0.85)}`,
        },
        "&:active": {
          transform: "translateY(0) scale(0.99)",
        },
        "&:hover .tab-icon": {
          transform: selected ? "rotate(-4deg) scale(1.08)" : "scale(1.08)",
        },
        "&:focus-visible": {
          outline: `2px solid ${accent.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        className="tab-icon"
        sx={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          background: selected ? accent.gradient : alpha(accent.main, isDark ? 0.18 : 0.14),
          color: selected ? "#fff" : accent.main,
          boxShadow: selected ? `0 4px 12px ${accent.glow}` : "none",
          transition: "background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>

      <Typography
        component="span"
        variant="body2"
        sx={{
          fontWeight: selected ? 800 : 600,
          fontSize: "0.875rem",
          whiteSpace: "nowrap",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          position: "relative",
          zIndex: 1,
        }}
      >
        {label}
        {!cat.ready && (
          <Chip
            label={lang?.videoStudioSoon || "Soon"}
            size="small"
            sx={{
              height: 18,
              fontSize: "0.65rem",
              fontWeight: 700,
              pointerEvents: "none",
              bgcolor: alpha(accent.main, 0.15),
              color: accent.main,
            }}
          />
        )}
      </Typography>

      {selected && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: "12%",
            right: "12%",
            height: 3,
            borderRadius: "3px 3px 0 0",
            background: accent.gradient,
            boxShadow: `0 0 10px ${accent.glow}`,
          }}
        />
      )}
    </Box>
  );
}

export default function CategorySidebar({
  categories,
  selectedId,
  onSelect,
  lang,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: 2.5,
        border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.6 : 1)}`,
        background: isDark
          ? `linear-gradient(145deg, ${alpha("#1a1a2e", 0.95)} 0%, ${alpha("#16213e", 0.9)} 50%, ${alpha("#0f3460", 0.85)} 100%)`
          : `linear-gradient(145deg, ${alpha("#F8FAFC", 1)} 0%, ${alpha("#EEF2FF", 0.85)} 50%, ${alpha("#FDF2F8", 0.7)} 100%)`,
        overflow: "hidden",
        boxShadow: isDark
          ? "0 8px 32px rgba(0, 0, 0, 0.35)"
          : "0 4px 20px rgba(99, 102, 241, 0.08)",
      }}
    >
      <Box
        role="tablist"
        aria-label={lang?.videoStudioUseCases || "Use cases"}
        sx={{
          display: "flex",
          gap: 1,
          px: { xs: 1, sm: 1.5 },
          py: 1.25,
          overflowX: "auto",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: 3,
            bgcolor: alpha(theme.palette.primary.main, 0.25),
          },
        }}
      >
        {categories.map((cat) => (
          <CategoryTab
            key={cat.id}
            cat={cat}
            selected={cat.id === selectedId}
            onSelect={onSelect}
            lang={lang}
            isDark={isDark}
          />
        ))}
      </Box>
    </Box>
  );
}
